// import { useNavigate } from 'react-router'
import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import type {
  Document,
  OfferProperty,
  PackageItemPropertyModel,
  PartPackageProperty,
  Variant
} from 'BrokerTsLib/src/dtos'

import {
  OfferPackageSettingsFormTabs
} from 'BrokerTsLib/src/dtos'

import { Insurer, PackageItemLimitType, Product, Risk } from 'BrokerTsLib/src/enums'
import { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { EditableTinySelect } from '@/containers/EditableTinySelect'
import { useApp } from '@/hooks/useApp'

import { baseUrl } from '@/hooks'
import { ProductSheetsPropertyInsurers } from '@/hooks/useProductSheets'
import { LoadingOverlay, LoadingOverlayContext } from '../../../components/LoadingOverlay'
import { useOfferGrid } from '../../../context/OfferGridContext'
import { usePropertyOffersContext } from '../../../context/PropertyOffersProvider'
import { offerStatusColors } from '../../../data/offers/utils/offerStatusColors'
import { useGetOfferDocuments } from '../../../hooks/useDocuments'
import { useGetGroupedRisks } from '../../../hooks/useEnumProperty'
import {
  useClone,
  useGetOfferAvailableAddons,
  useSelectCalculation,
  useSetOfferIncluded
} from '../../../hooks/useOffer'
import { useGetAvailablePackages, useGetGrouppedAvailablePackages, useGetOfferAvailablePackageItems, useSaveOfferParts, useUpdateOfferPackage } from '../../../hooks/useOfferPackage'
import { useUpdateLastWizardStep } from '../../../hooks/useWizard'
import { Check, Document as DocumentIcon, Duplicate, EditFilled, Pdf, Refresh, Settings } from '../../../icons'
import { useInsuranceParamsContext } from '../../../layouts/Wizard'
import { getLogo } from '../../../utils/companiesLogos'
import { OfferDocuments } from '../OfferDocuments'
import type { IBodyCell } from './columnSections'
import { DiscountCell } from './components/DiscountCell'
import { changePartLimit, OfferCell } from './components/OfferCell'
import { OfferCellPayment } from './components/OfferCellPayment'
import { BlankOfferCell } from './components/OfferCellWrapper'
import { OfferDocumentsModal } from './OfferDocumentsModal'
import { OfferPackageColumnSection } from './OfferPackageColumnSection'
import { OfferPackageSettingsForm } from './OfferPackageSettingsForm'
import { excludeRisksInGrid } from './OfferPropertyHeaderColumn'
import { StatusBadge } from './StatusBadge'

const documentUrl = `${baseUrl}/${import.meta.env.VITE_DOCUMENT_PROPERTY_API}`

const OfferColumnSection = ({ children, sx, ...rest }: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 2,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export interface OfferProps {
  offer: OfferProperty
  onToggle?: (included: boolean) => void
  variant: Variant
}

export const OfferPropertyColumn = ({ offer, onToggle, variant }: OfferProps) => {
  const { product } = useApp()
  const isLiability = product == Product.LiabilityInsurance
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentLiabilityPackageId, setCurrentLiabilityPackageId] = useState(offer.liabilityPackageId)

  const insuranceGroups = useGetGroupedRisks(variant, excludeRisksInGrid)
  const { data: availablePackagesData } = useGetAvailablePackages(offer.offerId)
  const { mutateAsync: updatePackage } = useUpdateOfferPackage()

  const availablePackages = availablePackagesData?.data.map(x => { return { value: x.name, id: x.id, groupId: x.value } }) ?? []
  const groupedAvailablePackages = useGetGrouppedAvailablePackages(offer.offerId)

  const hasAnyPackageToChange = Object.values(groupedAvailablePackages).some(group => group.length > 1)

  const responsibility = insuranceGroups['0']?.find((x) => x.id == Risk.RESPONSIBILITY)
  const responsibilityRealestate = insuranceGroups['0']?.find(
    (x) => x.id == Risk.RESPONSIBILITY_REALESTATE,
  )

  const spolProperty = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_PROPERTY)
  const spolFlood = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_FLOOD)
  const spolResponsibility = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_RESPONSIBILITY)

  const { insuranceParamsId, insuranceParams, refetchInsuranceParams } = useInsuranceParamsContext()
  const {
    updateOffer,
    calculatingOffersIds,
    recalculateOffers: recalculate,
  } = usePropertyOffersContext()
  const { data: dataOfferAvailableAddons } = useGetOfferAvailableAddons(offer!.offerId)
  const { data: dataOfferAvailablePackageItems, refetch: getOfferAvailablePackageItems } = useGetOfferAvailablePackageItems(offer!.offerId)
  const addons = (dataOfferAvailableAddons?.data ?? []) as PackageItemPropertyModel[]
  const packageItems = (dataOfferAvailablePackageItems?.data ?? []) as PackageItemPropertyModel[]

  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { setLoading } = useContext(LoadingOverlayContext)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: selectOffer } = useSelectCalculation()
  const { mutateAsync: saveOfferParts } = useSaveOfferParts()
  const { mutateAsync: cloneOffer } = useClone(offer.offerId)
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { openedColumns, onSectionTitleClick } = useOfferGrid()

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const displayId = `${offer.offerId}/${insuranceParamsId}`
  const elementId = `offer-${displayId.replace(/\//, '')}`

  const hasError = (offer.errors?.length ?? 0) > 0
  const errMsg = hasError ? offer.errors?.map((e) => e.message).join('<br/>') : ''

  let status = OfferStatusEnum.Valid
  if (hasError) status = OfferStatusEnum.Error
  else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId)
    status = OfferStatusEnum.Selected

  const color = offerStatusColors[status]
  const logo = getLogo(offer.insurerId)

  const { mutateAsync: setIncluded } = useSetOfferIncluded()

  const setAllLimits = (limit: number | 'max') => {
    const ignoredRisks = [
      Risk.SPOL_PROPERTY,
      Risk.SPOL_FLOOD,
      Risk.SPOL_RESPONSIBILITY,
      Risk.SECONDARY_BUILDINGS,
      // vyjimky viz CHALOUPKA-727
      Risk.JEWERLY,
      Risk.MECHANICAL_AND_ELECTRIC_DEVICE,
      Risk.NONRESIDENTAL_THINGS,
      Risk.VALUABLES,
      Risk.BUILDING_COMPONENTS,
      Risk.GARDEN_FLORA,
    ]

    offer.parts
      .filter((op) => op.packageItem.limitType == PackageItemLimitType.Limit &&
        op.packageItem.variants.length > 0 && op.included && !ignoredRisks.includes(op.riskId))
      .forEach((op) => {
        const variants = op.packageItem.variants
          // .filter(x => x.limitEditable)
          .map(x => {
            return {
              limit: x.maxLimit ?? x.defaultLimit!,
              variant: x
            }
          })
          .sort((a, b) => a.limit - b.limit) // serazení podle limitu od nejmenšího po největší

        if (variants.length == 0)
          return

        // nalezeni varianty
        let variant = null

        // pokud hledam max limit, tak vyberu posledni variantu
        if (limit == 'max') {
          variant = variants[variants.length - 1]
        } else {
          // v opacnem pripade vyberu variantu podle limitu
          variant = variants.find(x =>
            // bud presna shoda
            x.limit == limit ||
            // nebo je limit editovatelny a pak je alespon v rozmezi min a max limitu
            x.variant.limitEditable && (x.variant.minLimit ?? 0) <= limit && (x.variant.maxLimit ?? limit) >= limit)
            ?? variants.find(x => x.limit > limit) // nejdrive prvni vetsi
            ?? variants.reverse().find(x => x.limit < limit) // pokud nenajde, tak prvni mensi

          if (variant?.variant.limitEditable)
            variant.limit = limit
        }

        // pokud uz takovy limit je nastaveny, tak nic neresim
        if (op.packageItemVariantId == variant?.variant.id && op.limit == variant?.limit)
          return

        changePartLimit(op, op.included, variant?.limit, variant?.variant.id)
      })

    setLoading(true)
    if (offer.parts.findIndex((x) => !!x.changed) >= 0) {
      updateOffer(offer)
    } else {
      // pouze pro efekt, aby bylo videt, ze se neco stalo
      setTimeout(() => {
        setLoading(false)
      }, 200)
    }
  }

  const handleToggle = (included: boolean) => {
    onToggle?.(included)

    setTimeout(() => {
      offer!.included = included
      updateOffer(offer!)
      setIncluded({ offerId: offer!.offerId!, included: included })
    }, 300)
  }

  const saveCurrentState = () => {
    setLoading(true)
    saveOfferParts(offer)
      .then(() => recalculate([offer.offerId], true))
      .finally(() => setLoading(false))
  }

  const [offerDocuments, setOfferDocuments] = useState<Document[]>([])
  const { refetch: getOfferDocuments, data: offerDocumentsData } = useGetOfferDocuments(offer!.offerId, { enabled: false })

  useEffect(() => {
    if (offerDocumentsData !== undefined) {
      if (offerDocumentsData.data?.length > 0) setOfferDocuments([...offerDocumentsData.data])
      else setOfferDocuments([])
    }
  }, [offerDocumentsData])

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    setLoading(isRecalculating)

    if (!isRecalculating) {
      // je pred timeoutem, aby se mi po 5 vterinach nezmenily limity apod. Ale aby se to projevilo hned
      getOfferAvailablePackageItems() // i limity v balíčcích se mohou změnit vlivem změny jiných offer partů (vedlejší stavby, cennosti, ...)

      setTimeout(() => {
        getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
        //getOfferAvailableDiscounts() // slevy se mohou po rekalkulaci zmenit
      }, 5000)
    }
  }, [isRecalculating])

  const offerDoc = offerDocuments.find((x) => x.name === 'OfferDocument')
  const changed = offer.parts.findIndex((x) => !!x.changed) >= 0

  useEffect(() => {
    if (changed) {
      saveCurrentState()
    }
  }, [changed])

  const [showSettingTab, setShowSettingTab] = useState<OfferPackageSettingsFormTabs | null>(null)
  const onOpenSettings = (showTab: OfferPackageSettingsFormTabs) => {
    setShowSettingTab(showTab)
    onOpen()
  }

  const { isOpen: isDocumentsModalOpen, onClose: onDocumentsModalClose, onOpen: onDocumentsModalOpen } = useDisclosure()

  if (!packageItems || packageItems.length == 0)
    return null

  let responsibilityOfferPart = offer.parts.find((op) => op.riskId == Risk.RESPONSIBILITY)
  const responsibilityPackageItem = packageItems.find((x) => x.riskId == Risk.RESPONSIBILITY)

  let responsibilityRealestateOfferPart = offer.parts.find(
    (op) => op.riskId == Risk.RESPONSIBILITY_REALESTATE,
  )
  const responsibilityRealestatePackageItem = packageItems.find(
    (x) => x.riskId == Risk.RESPONSIBILITY_REALESTATE,
  )

  let spolPropertyOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_PROPERTY)!
  const spolPropertyPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_PROPERTY)

  if (!spolPropertyOfferPart && !isLiability) {
    spolPropertyOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_PROPERTY,
      offerId: offer.offerId,
      packageItem: spolPropertyPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolPropertyOfferPart)
  }

  let spolResponsibilityOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_RESPONSIBILITY)
  const spolResponsibilityPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_RESPONSIBILITY)

  if (!spolResponsibilityOfferPart) {
    spolResponsibilityOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_RESPONSIBILITY,
      offerId: offer.offerId,
      packageItem: spolResponsibilityPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolResponsibilityOfferPart)
  }

  const spolPropertyOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_PROPERTY)
  const spolPropertyPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_PROPERTY)

  let spolFloodOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_FLOOD)

  if (!spolFloodOfferPart && !isLiability) {
    spolFloodOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_FLOOD,
      offerId: offer.offerId,
      packageItem: spolFloodPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolFloodOfferPart)
  }

  const spolFloodOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_FLOOD)
  const floodRiskIncluded = offer.parts.some((op) => op.riskId == Risk.FLOOD && op.included)

  if (!responsibilityOfferPart) {
    responsibilityOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY,
      offerId: offer.offerId,
      packageItem: responsibilityPackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityOfferPart)
  }

  if (!responsibilityRealestateOfferPart) {
    responsibilityRealestateOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY_REALESTATE,
      offerId: offer.offerId,
      packageItem: responsibilityRealestatePackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityRealestateOfferPart)
  }

  const itemPerGroupKey = Object.keys(insuranceGroups ?? {})
    .map(key => {
      const items = insuranceGroups[key]
        .filter(
          (x) =>
            ![
              Risk.RESPONSIBILITY,
              Risk.RESPONSIBILITY_REALESTATE,
              Risk.SPOL_PROPERTY,
              Risk.SPOL_FLOOD,
              Risk.SPOL_RESPONSIBILITY,
            ].includes(x.id),
        )

      return { items, key }
    })
    .filter(x => x.items.length > 0)

  const title = [...new Set([offer.householdPackageName, offer.realtyPackageName, offer.liabilityPackageName].filter(x => !!x))].join(' + ')

  return (
    <LoadingOverlay isLoading={isSubmitting}>
      <Box
        id={elementId}
        sx={{
          display: 'grid',
          gap: 'px',
          placeContent: 'stretch',
          borderLeft: '1px solid',
          borderLeftColor: 'gray.300',
          minW: 230,
        }}
      >
        {/* id, included... */}
        <OfferColumnSection
          sx={{
            px: 2,
            py: 1,
            bg: color,
          }}
        >
          <Flex sx={{ gap: 2 }}>
            <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{`#${displayId}`}</Text>
            {status && <StatusBadge status={status} tooltip={errMsg} />}
          </Flex>

          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Switch
              id={`offer-toggle-${displayId}`}
              size="sm"
              isDisabled={isContracted}
              isChecked={offer?.included}
              onChange={(e) => handleToggle(e.target.checked)}
            />

            {/* <Flex>
              <IconButton bg="transparent" aria-label="Posunout doleva">
                <ChevronLeft width={3} height={3} />
              </IconButton>
              <IconButton bg="transparent" aria-label="Posunout doprava">
                <ChevronRight width={3} height={3} />
              </IconButton>
            </Flex> */}
          </Flex>
        </OfferColumnSection>

        {/* logo, sjednat button */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
            py: 0,
            height: '52px'
          }}
        >
          <Image
            src={logo}
            alt={offer.insurerName}
            width="64px"
            maxH="48px"
            style={{ mixBlendMode: 'multiply' }}
          />

          {status !== OfferStatusEnum.Error && !isContracted && !changed && (
            <Button
              colorScheme="primary"
              size="sm"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Ukládám..."
              onClick={() => {
                setIsSubmitting(true)

                selectOffer({ paramsId: insuranceParamsId!, offerId: offer.offerId })
                  .then(() => {
                    if ((insuranceParams?.generalParams.lastWizardStep ?? 3) < 4)
                      updateStep({ paramsId: Number(insuranceParamsId!), step: 4 }).then(() =>
                        refetchInsuranceParams().then(() => {
                          navigate('/chaloupka/wizard/4/' + insuranceParamsId!)
                        }),
                      )
                    else
                      refetchInsuranceParams().then(() => {
                        navigate('/chaloupka/wizard/4/' + insuranceParamsId!)
                      })
                  })
                  .finally(() => setIsSubmitting(false))
              }}
            >
              <Check />
              Sjednat
            </Button>
          )}

          {/* {changed && (
            <Button colorScheme="blue" size="sm" onClick={saveCurrentState}>
              <Refresh />
              Přepočítat
            </Button>
          )} */}

          {status === OfferStatusEnum.Error && (
            <Tooltip
              label={
                <Stack direction={'column'}>
                  {offer?.errors.map((e, i) => {
                    return (
                      <Text key={i} variant="calcError" whiteSpace={'break-spaces'}>
                        {e.message}
                      </Text>
                    )
                  })}
                </Stack>
              }
              hasArrow
              placement="top"
            >
              <Text sx={{ color: 'red.800', fontSize: 'xs', fontWeight: 'semibold' }}>Chyba</Text>
            </Tooltip>
          )}
        </OfferColumnSection>

        {/* price */}
        <OfferColumnSection
          sx={{
            p: 2,
            bg: color,
            gap: 2,
          }}
        >

          {product == Product.LiabilityInsurance &&
            <EditableTinySelect
              variants={availablePackages}
              isDisabled={isContracted || !hasAnyPackageToChange}
              onSubmit={(value: string, id: number) => {
                setCurrentLiabilityPackageId(id)
                updatePackage({ offerId: offer.offerId, packages: [{ packageId: id, groupId: 5 }] })
                  .then(() => {
                    recalculate([offer.offerId], true)
                      .then(() => { getOfferAvailablePackageItems() })
                  })
              }}
              value={currentLiabilityPackageId?.toString() ?? ''}
            />
          }

          {product == Product.PropertyInsurance &&
            <>
              {!hasAnyPackageToChange
                ?
                <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>
                  {title}
                </Text>
                :
                <Flex gap={1} sx={{ cursor: 'pointer', alignItems: 'center' }} onClick={(e) => onOpenSettings(OfferPackageSettingsFormTabs.packages)}>
                  <Text sx={{ fontSize: 'sm', fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                    {title}
                  </Text>
                  <EditFilled width={4} height={4} color="gray.500" />
                </Flex>
              }
            </>
          }

          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {changed ? '???' : formatPrice({ price: offer.price })}
          </Text>
        </OfferColumnSection>

        {/* buttons */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
          }}
        >
          {/* <Stars /> */}

          <Flex sx={{ gap: 1, flex: 1, justifyContent: 'end', alignItems: 'center' }}>
            {offer.insurerId == Insurer.GeneraliCeskaPojistovna &&
              <>
                <Tooltip label="Nastavit maximální limity" hasArrow placement="top">
                  <Button size='sm' variant='solid' color='secondary.500' onClick={() => setAllLimits('max')} py={0}>
                    Max. limity
                  </Button>
                </Tooltip>

                <Tooltip label="Nastavit limity na 100 000 Kč" hasArrow placement="top">
                  <Button size='sm' variant='solid' color='secondary.500' onClick={() => setAllLimits(100000)} py={0}>
                    100 000 Kč
                  </Button>
                </Tooltip>
              </>
            }

            <OfferDocuments offerId={offer.offerId} variant="ghost" colorScheme="gray" color="gray.500" size="sm" py={0} />

            {offerDoc && (
              <Tooltip label="Stáhnout PDF nabídku pojišťovny" hasArrow placement="top">
                <IconButton
                  variant="ghost"
                  aria-label="PDF"
                  as={'a'}
                  target="_blank"
                  href={`${documentUrl}/GetOfferDocument?docId=${offerDoc.id}`}
                >
                  <Pdf color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}

            {ProductSheetsPropertyInsurers.includes(offer.insurerId) && (
              <Tooltip label="Dokumenty" hasArrow placement="top">
                <IconButton
                  variant="ghost"
                  aria-label="Dokumenty"
                  onClick={onDocumentsModalOpen}
                >
                  <DocumentIcon color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}

            {!isContracted && (
              <>
                <Tooltip label="Přepočítat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Obnovit"
                    onClick={() => {
                      recalculate([offer.offerId], true).finally(() => {
                        getOfferDocuments()
                      })
                    }}
                  >
                    <Refresh color="secondary.500" />
                  </IconButton>
                </Tooltip>

                <Tooltip label="Klonovat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Klonovat"
                    onClick={() => {
                      cloneOffer().then(({ data: offer }) => {
                        updateOffer(offer as OfferProperty)
                        recalculate([offer.offerId], true)
                      })
                    }}
                  >
                    <Duplicate color="secondary.500" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {addons.length > 0 && (
              <Tooltip label="Další doplňková pojištění" hasArrow placement="top">
                <IconButton variant="ghost" aria-label="Nastavení" onClick={() => onOpenSettings(OfferPackageSettingsFormTabs.addons)}>
                  <Settings color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}
          </Flex>
        </OfferColumnSection>

        {/* risks in groups */}
        {itemPerGroupKey && itemPerGroupKey.length > 0 && itemPerGroupKey.map((x) => {
          const items = x.items
          const section = items[0]

          let titleHouseholdAndRealty: React.ReactNode = null
          if (section.groupId == 0) {
            const ids = items.map((x) => x.id)
            let isHouseholdAndRealty = false

            ids.forEach((riskId) => {
              if (offer.parts.filter((op) => op.packageItem?.riskId == riskId).length > 1) {
                isHouseholdAndRealty = true
              }
            })
            if (isHouseholdAndRealty) {
              titleHouseholdAndRealty = (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    justifyContent: 'end',
                    width: '100%',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>Domácnost</Box>
                  <Box sx={{ textAlign: 'center' }}>Stavba</Box>
                </Box>
              )
            }
          }

          return (
            <OfferPackageColumnSection
              onClick={() => {
                onSectionTitleClick(section.groupId)
              }}
              isOpened={openedColumns.includes(section.groupId)}
              key={section.groupId}
              titleBg={color}
              titleWhenOpened={titleHouseholdAndRealty}
              bodyCells={items.map((x) => {
                const bodyCells = packageItems
                  .filter((pi) => pi.riskId == x.id)
                  .sort(
                    (x, y) =>
                      (x.productTypeRiskVariantId ?? 0) - (y.productTypeRiskVariantId ?? 0),
                  )
                  .map((pi) => {
                    let op = offer.parts.find((op) => op.packageItem?.packageItemId == pi?.packageItemId)
                    if (!op) {
                      op = {
                        partId: 0,
                        riskId: x.id,
                        offerId: offer.offerId,
                        packageItem: pi,
                      } as PartPackageProperty

                      offer.parts.push(op)
                    }

                    return {
                      id: x.id.toString(),
                      offerPart: op,
                      packageItem: pi,
                    } as IBodyCell
                  })

                return bodyCells
              })}
            />
          )
        })}

        {responsibility && responsibilityPackageItem
          ? (
            <OfferCell
              id={Risk.RESPONSIBILITY.toString()}
              offerPart={responsibilityOfferPart}
              packageItem={responsibilityPackageItem}
            />
          )
          : <BlankOfferCell />
        }


        {responsibilityRealestate && responsibilityRealestatePackageItem
          ? (
            <OfferCell
              id={Risk.RESPONSIBILITY_REALESTATE.toString()}
              offerPart={responsibilityRealestateOfferPart}
              packageItem={responsibilityRealestatePackageItem}
            />
          )
          : <BlankOfferCell />}

        {spolProperty && spolPropertyOfferParts.length > 1 && (
          <Box
            key={'spolBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolPropertyOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {spolPropertyOfferParts
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => {
                const spolPropertyPackageItem = spolPropertyPackageItems.find(
                  (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                )
                return spolPropertyPackageItem ? (
                  <OfferCell
                    key={i}
                    id={Risk.SPOL_PROPERTY.toString()}
                    offerPart={spolOfferPart}
                    packageItem={spolPropertyPackageItem}
                    singleCell={false}
                  />
                ) : undefined
              })}
          </Box>
        )}

        {spolProperty && spolPropertyOfferParts.length == 1 && spolPropertyPackageItem && (
          <OfferCell
            id={Risk.SPOL_PROPERTY.toString()}
            offerPart={spolPropertyOfferPart}
            packageItem={spolPropertyPackageItem}
          />
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length > 1 && (
          <Box
            key={'spolFoodBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolFloodOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {spolFloodOfferParts
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => {
                const spolFloodPackageItem = spolFloodPackageItems.find(
                  (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                )
                return spolFloodPackageItem ? <OfferCell
                  key={i}
                  id={Risk.SPOL_FLOOD.toString()}
                  offerPart={spolOfferPart}
                  packageItem={
                    spolFloodPackageItems.find(
                      (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                    )!
                  }
                  singleCell={false}
                /> : undefined
              })}
          </Box>
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length == 1 && !isLiability && spolFloodPackageItem && (
          <OfferCell
            id={Risk.SPOL_FLOOD.toString()}
            offerPart={spolFloodOfferPart!}
            packageItem={spolFloodPackageItem}
          />
        )}

        {/* pokud nabídka neobsahuje povodeň, zobrazujeme pouze prázdnou buňku */}
        {spolFlood && !floodRiskIncluded && <BlankOfferCell />}

        {spolResponsibility && spolResponsibilityPackageItem &&
          <OfferCell
            id={Risk.SPOL_RESPONSIBILITY.toString()}
            offerPart={spolResponsibilityOfferPart}
            packageItem={spolResponsibilityPackageItem}
          />
        }

        {spolResponsibility && !spolResponsibilityPackageItem && <BlankOfferCell />}

        <DiscountCell offer={offer} isDisabled={isContracted} />

        <OfferCellPayment offerId={offer.offerId} isDisabled={isContracted} />
      </Box>

      <OfferPackageSettingsForm
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={() => recalculate([offer.offerId])}
        offer={offer}
        title={`Nastavení nabídky #${displayId}`}
        isReadOnly={isContracted}
        activeTab={showSettingTab}
      />

      <OfferDocumentsModal isOpen={isDocumentsModalOpen} onClose={onDocumentsModalClose} offer={offer} />

    </LoadingOverlay>
  )
}
